import { Icon } from '@iconify-icon/react';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import { DocsArticleLayout } from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

const tableOfContents = [
  { id: 'why-private-apps', title: '1. Why Private Apps Need a Different Workflow' },
  { id: 'thirty-minute-plan', title: '2. The 30-Minute MCP Plan' },
  { id: 'inspect-logic', title: '3. Step 1: Inspect the Product Logic' },
  { id: 'export-data', title: '4. Step 2: Export Structured Live Data' },
  { id: 'train-in-layers', title: '5. Step 3: Train in Layers' },
  { id: 'prompt-and-ux', title: '6. Step 4: Add Prompt Rules and UX' },
  { id: 'stakeholder-testing', title: '7. Step 5: Test with Stakeholder Questions' },
  { id: 'security', title: '8. Keep Sensitive Data Out of the Workflow' },
];

const PrivateAppChatbotMcpPage = () => {
  return (
    <>
      <NextSeo
        title="Build a Private-App Chatbot with MCP in 30 Minutes"
        description="Learn a practical MCP workflow for turning a private app or data-heavy dashboard into a trained Chat.co chatbot in about 30 minutes."
        canonical="https://chat.co/docs/private-app-chatbot-mcp"
        openGraph={{
          url: 'https://chat.co/docs/private-app-chatbot-mcp',
          title: 'Build a Private-App Chatbot with MCP in 30 Minutes | Chat.co Docs',
          description:
            'Learn a practical MCP workflow for turning a private app or data-heavy dashboard into a trained Chat.co chatbot in about 30 minutes.',
          type: 'article',
          article: {
            publishedTime: '2026-03-09T00:00:00Z',
            modifiedTime: '2026-03-09T00:00:00Z',
            section: 'Tutorials',
            tags: ['mcp', 'private app', 'structured data', 'chatbot training', 'tutorial'],
          },
          images: [
            {
              url: 'https://chat.co/images/og-docs.png',
              width: 1200,
              height: 630,
              alt: 'Chat.co Private-App MCP Tutorial',
            },
          ],
        }}
        twitter={{
          cardType: 'summary_large_image',
        }}
      />

      <DocsArticleLayout
        category="Tutorials"
        categoryColor="text-blue-700"
        title="Build a Private-App Chatbot with MCP in 30 Minutes"
        description="Use MCP to inspect a private app, package the real source of truth, and train a Chat.co bot that answers the questions stakeholders actually ask."
        readingTime="14 min read"
        lastUpdated="March 2026"
        tableOfContents={tableOfContents}
        prevArticle={{
          title: 'FAQ to AI Expert Migration',
          href: PageLink.docsFaqMigration,
        }}
        nextArticle={{
          title: 'Managing Multiple Bots & Teams',
          href: PageLink.docsMultiBotOrchestration,
        }}
        keyTakeaways={[
          'For private SPAs and dashboards, start from structured data instead of a simple crawl',
          'MCP lets an AI coding assistant configure the full Chat.co workflow in one working session',
          'Layer documents, text training, and curated Q&A instead of relying on uploads alone',
          'Prompt rules and appearance settings matter as much as the source files',
          'Validate with stakeholder questions, not only developer lookups',
        ]}>
        <p className="lead text-lg text-gray-700">
          Many teams try to train a chatbot for a private application by crawling a few URLs and
          hoping the model will infer the rest. That usually breaks down for data-heavy products,
          dashboards, and logged-in experiences. This tutorial shows a better workflow: use{' '}
          <Link
            href={PageLink.docsMcpAgentSetup}
            className="font-medium text-primary-600 hover:underline">
            Chat.co MCP
          </Link>{' '}
          to inspect the product logic, export the real source of truth, and train the bot start to
          finish in about 30 minutes.
        </p>

        <div className="my-8 rounded-xl border border-blue-200 bg-blue-50 p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
              <Icon icon="tabler:clock-hour-4" className="text-xl text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-blue-900">Real Workflow, Condensed for Docs</h4>
              <p className="mt-1 text-blue-800">
                This guide is based on a real build completed with ChatGPT 5.4 using Chat.co MCP.
                The exact assistant can vary, but the workflow stays the same: inspect the app,
                export structured data, train the bot in layers, and validate with real business
                questions.
              </p>
            </div>
          </div>
        </div>

        <section id="why-private-apps" className="scroll-mt-24">
          <h2>1. Why Private Apps Need a Different Workflow</h2>

          <p>
            Public marketing sites are often crawl-friendly. Private production apps are usually
            not. In many modern products, the visible UI is only a shell around live data,
            calculated summaries, and business rules that exist in code.
          </p>

          <div className="my-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <h4 className="flex items-center gap-2 font-semibold text-red-900">
                <Icon icon="tabler:x" className="text-lg" />
                Crawl-only approach
              </h4>
              <ul className="mt-3 space-y-1 text-sm text-red-800">
                <li>Indexes labels, navigation, and empty state shells</li>
                <li>Misses private or runtime-only data</li>
                <li>Understands screens, but not calculation logic</li>
                <li>Breaks on domain-specific questions about totals and scenarios</li>
              </ul>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <h4 className="flex items-center gap-2 font-semibold text-green-900">
                <Icon icon="tabler:check" className="text-lg" />
                MCP + structured export
              </h4>
              <ul className="mt-3 space-y-1 text-sm text-green-800">
                <li>Starts from the actual data model and transformation logic</li>
                <li>Captures the meaning behind the dashboard numbers</li>
                <li>Lets an AI assistant configure Chat.co directly through tools</li>
                <li>Produces answers that match how operators already discuss the product</li>
              </ul>
            </div>
          </div>

          <p>
            If your users ask questions about what changed versus recommended, which category drives
            the biggest shift, or how much was moved to federal funding, a simple crawl is rarely
            enough.
          </p>
        </section>

        <section id="thirty-minute-plan" className="scroll-mt-24">
          <h2>2. The 30-Minute MCP Plan</h2>

          <p>
            The speed comes from using MCP as the working bridge between your AI assistant and
            Chat.co. Instead of switching between a code editor, exports folder, and dashboard tabs,
            you can move through the setup as one guided session.
          </p>

          <div className="my-6 grid gap-4 sm:grid-cols-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-center">
              <div className="text-2xl font-bold text-primary-600">5 min</div>
              <div className="mt-1 text-sm font-medium text-gray-900">Map the logic</div>
              <p className="mt-2 text-xs text-gray-600">
                Inspect the codebase and identify how the product calculates key numbers.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-center">
              <div className="text-2xl font-bold text-primary-600">8 min</div>
              <div className="mt-1 text-sm font-medium text-gray-900">Export the truth</div>
              <p className="mt-2 text-xs text-gray-600">
                Package the structured live data and add short narrative guides.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-center">
              <div className="text-2xl font-bold text-primary-600">12 min</div>
              <div className="mt-1 text-sm font-medium text-gray-900">Train through MCP</div>
              <p className="mt-2 text-xs text-gray-600">
                Upload documents, replace text training, seed Q&amp;A, and tune the bot.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-center">
              <div className="text-2xl font-bold text-primary-600">5 min</div>
              <div className="mt-1 text-sm font-medium text-gray-900">Validate with users</div>
              <p className="mt-2 text-xs text-gray-600">
                Test against stakeholder questions and fix the layer that failed.
              </p>
            </div>
          </div>

          <div className="my-6 rounded-lg border border-amber-200 bg-amber-50 p-5">
            <p className="text-sm text-amber-900">
              <strong>Before you start:</strong> set up MCP first. If you still need the local
              connection and approval flow, read{' '}
              <Link href={PageLink.docsMcpAgentSetup} className="underline">
                MCP Agent Setup
              </Link>{' '}
              and{' '}
              <Link href={PageLink.docsApiVsMcp} className="underline">
                API vs MCP
              </Link>
              .
            </p>
          </div>
        </section>

        <section id="inspect-logic" className="scroll-mt-24">
          <h2>3. Step 1: Inspect the Product Logic</h2>

          <p>
            Start by using your AI coding assistant to inspect the application codebase. The goal is
            not to train the chatbot on code directly. The goal is to understand how the product
            thinks before you package any training content.
          </p>

          <h3>Questions to answer first</h3>
          <ul>
            <li>Where does the dashboard get its numbers?</li>
            <li>Which hooks, services, or queries compute the KPI cards?</li>
            <li>Are totals raw sums, filtered sums, or adjusted values?</li>
            <li>Which exclusions, fallback rules, or scenario switches affect the results?</li>
            <li>Which identifiers should be treated as first-class lookup keys?</li>
          </ul>

          <p>
            This step prevents a common failure mode: the bot retrieves the right record but answers
            the wrong business question because it never learned how the dashboard defines the
            numbers.
          </p>
        </section>

        <section id="export-data" className="scroll-mt-24">
          <h2>4. Step 2: Export Structured Live Data</h2>

          <p>
            Once you understand the product logic, choose the right source material. For a private
            SPA, internal dashboard, or data-heavy workflow app, the best primary source is often a
            structured export rather than HTML.
          </p>

          <h3>Good export package</h3>
          <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
            <pre className="text-sm text-gray-300">{`entities.json
fiscal-years.json
funding-sources.json
line-items.json
line-item-values.json
event-history.json
rate-parameters.json
budget-summaries.json
overview.md
decisionmaker-guide.md`}</pre>
          </div>

          <p>
            The structured files give the bot factual coverage. The two short narrative files supply
            context: what the dataset contains and how to interpret the main dashboard visuals.
          </p>

          <div className="my-6 rounded-lg border border-gray-200 bg-white p-5">
            <h4 className="font-semibold text-gray-900">Decision rule</h4>
            <p className="mt-2 text-sm text-gray-600">
              If your product is public and mostly static, crawl first. If it is authenticated,
              client-rendered, or driven by live runtime data, export structured data first and use
              crawl only as a supporting source.
            </p>
          </div>
        </section>

        <section id="train-in-layers" className="scroll-mt-24">
          <h2>5. Step 3: Train in Layers</h2>

          <p>
            The strongest setup is layered. Do not stop after document upload. Use MCP to push the
            bot through three distinct training layers.
          </p>

          <div className="my-6 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">Layer 1: Structured documents</h4>
              <p className="mt-2 text-sm text-gray-600">
                Upload the source files so the bot can answer lookups about entities, codes, fiscal
                year values, funding assignments, and line-item detail.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">Layer 2: Text training</h4>
              <p className="mt-2 text-sm text-gray-600">
                Add short text definitions that explain business meaning: what counts as recommended
                versus petitioned, what moved to federal means, and which totals belong to which
                scenario.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">
                Layer 3: Exact Q&amp;A for real prompts
              </h4>
              <p className="mt-2 text-sm text-gray-600">
                Seed the bot with the exact high-value questions stakeholders ask in meetings. This
                is often the biggest jump in answer quality.
              </p>
            </div>
          </div>

          <h3>MCP workflow shape</h3>
          <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
            <pre className="text-sm text-gray-300">{`1. Create or select the chatbot
2. Set the text prompt
3. Upload structured documents
4. Replace text training
5. Bulk import curated Q&A
6. Update appearance and starter questions
7. Validate with real user prompts`}</pre>
          </div>

          <p>
            The value of MCP here is speed and continuity. Your assistant can inspect the product,
            prepare the training assets, and call Chat.co tools without forcing you into a manual
            dashboard-only workflow.
          </p>
        </section>

        <section id="prompt-and-ux" className="scroll-mt-24">
          <h2>6. Step 4: Add Prompt Rules and UX</h2>

          <p>
            A strong prompt is part of the training system. Without it, the bot can retrieve the
            right row and still answer the wrong question.
          </p>

          <h3>Prompt rules that usually matter</h3>
          <ul>
            <li>Default to the product&apos;s business framing, not generic summarization</li>
            <li>Separate broad totals from line-item lookup answers</li>
            <li>Label fiscal year, scenario, and comparison basis clearly</li>
            <li>Do not mix recommended, petitioned, baseline, or current values</li>
            <li>Treat codes, line numbers, and entity names as search keys</li>
            <li>Refuse to invent missing numbers</li>
          </ul>

          <h3>Do not ignore presentation</h3>
          <p>
            Once the content is reliable, align the bot with the product experience users already
            trust:
          </p>
          <ul>
            <li>Title and welcome message</li>
            <li>Starter questions</li>
            <li>Brand colors and launcher behavior</li>
            <li>Citation display expectations</li>
          </ul>

          <div className="my-6 rounded-lg border border-blue-200 bg-blue-50 p-5">
            <p className="text-sm text-blue-900">
              <strong>Rule of thumb:</strong> keep the visual language consistent with the product,
              but write the welcome text for the specific job this bot is supposed to do.
            </p>
          </div>
        </section>

        <section id="stakeholder-testing" className="scroll-mt-24">
          <h2>7. Step 5: Test with Stakeholder Questions</h2>

          <p>
            Developer tests are too narrow. Real users ask broader, fuzzier questions that require
            interpretation and correct framing.
          </p>

          <div className="my-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">Developer-style checks</h4>
              <ul className="mt-3 space-y-1 text-sm text-gray-600">
                <li>What is code ABC-123?</li>
                <li>Which funding source maps to line item X?</li>
                <li>Show the value for a known entity and year.</li>
              </ul>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">Stakeholder-style checks</h4>
              <ul className="mt-3 space-y-1 text-sm text-gray-600">
                <li>How much are they asking for and what is being recommended?</li>
                <li>What changed versus the baseline scenario?</li>
                <li>Which category contributes most to the reduction?</li>
                <li>What is the customer impact if this shift stays in place?</li>
              </ul>
            </div>
          </div>

          <p>When a response fails, trace it back to the right layer:</p>
          <ul>
            <li>The bot had the data but not the interpretation</li>
            <li>The bot had the interpretation but not the exact phrasing</li>
            <li>The question was ambiguous and needed a stronger prompt rule</li>
          </ul>

          <p>That feedback loop is what turns a technically trained bot into a useful product.</p>
        </section>

        <section id="security" className="scroll-mt-24">
          <h2>8. Keep Sensitive Data Out of the Workflow</h2>

          <p>
            This workflow is especially useful for private apps, which means security discipline is
            part of the tutorial.
          </p>

          <ul>
            <li>Never commit API keys or local key files to the repo</li>
            <li>Rotate temporary credentials after setup or demos</li>
            <li>Redact account IDs, internal URLs, and private labels in public write-ups</li>
            <li>
              Keep training exports separate from the application repo when they contain live data
            </li>
            <li>
              Use placeholder screenshots and sample payloads unless you have explicit approval
            </li>
          </ul>

          <div className="my-6 rounded-xl border border-gray-200 bg-gray-50 p-6">
            <h4 className="font-semibold text-gray-900">Final takeaway</h4>
            <p className="mt-2 text-gray-700">
              The strongest Chat.co bots for private products are not built by training on the
              website alone. They are built by finding the real source of truth, teaching the bot
              how the product defines its numbers, and validating against the questions
              decision-makers actually ask. MCP compresses that work into one practical build
              session.
            </p>
          </div>

          <p>
            If you want to connect an AI client first, start with{' '}
            <Link
              href={PageLink.docsMcpAgentSetup}
              className="font-medium text-primary-600 hover:underline">
              MCP Agent Setup
            </Link>
            . If you want the broader positioning first, read{' '}
            <Link
              href={PageLink.docsApiVsMcp}
              className="font-medium text-primary-600 hover:underline">
              API vs MCP
            </Link>
            .
          </p>
        </section>
      </DocsArticleLayout>
    </>
  );
};

export default PrivateAppChatbotMcpPage;
